/**
 * Browse Steam playtests sheet (INVENTORY §2 row 6; prototype lines 1147-1187).
 *
 * `☆ TRACK` does not track directly — it routes through `ptFromBrowse`, which
 * pre-fills the add sheet with a synthetic "widget found" result so the
 * party-size gate still has to be answered (INVENTORY §9 footer: "TRACKING ASKS
 * FOR PARTY SIZE, THEN ADDS IT TO EVERYONE'S PLAYTESTS TAB"). An already-tracked
 * row toasts `ALREADY TRACKED` instead.
 *
 * The header claims 2,143 games and the footer says "OF 212"; both are frozen
 * copy and both are reproduced (INVENTORY §9, §12 item 8 — `PT_BROWSE` is still
 * four hard-coded rows).
 */

import { PtCover } from '../components/PtArt.tsx';
import { SearchBox } from '../components/SearchBox.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useQd } from '../store/index.ts';
import { ptBrowseVM } from '../store/selectors.3e.ts';

const MONO = 'var(--gt-font-mono)';

export default function PtBrowseSheet(): JSX.Element {
  const s = useQd();
  const v = ptBrowseVM(s);

  return (
    <Sheet
      label="Browse steam sheet"
      maxHeight="90vh"
      hs
      onClose={s.closePtBrowse}
      header={{ title: '⊞ BROWSE STEAM PLAYTESTS', titleClr: 'var(--gt-text)', titleFs: '8.5px', note: '' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11, padding: '13px 14px 18px' }}>
        <div style={{ fontFamily: MONO, fontSize: 9, lineHeight: 1.7, color: 'var(--gt-muted)' }}>{v.head}</div>

        <SearchBox
          value={v.q}
          onChange={(e) => s.setPtBq(e.target.value)}
          placeholder="SEARCH THE CATALOG…"
          w="100%"
          ifs={11}
          pad="13px 12px"
        />

        {v.rows.map((b) => (
          <div
            key={b.key}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              border: '1px solid var(--gt-line)',
              background: 'var(--gt-tile)',
              padding: 11,
            }}
          >
            <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
              <div style={{ position: 'relative', width: 104, aspectRatio: '460/215', flex: 'none', background: b.fall, overflow: 'hidden' }}>
                <PtCover style={{ position: 'absolute', inset: 0 }} fs={9} id={'br' + b.appid} ini={b.ini} cands={b.cands} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '12.5px', color: 'var(--gt-text)' }}>{b.name}</div>
                <div style={{ fontSize: 10, color: 'var(--gt-text-5)' }}>{b.dev}</div>
                <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.03em', color: 'var(--gt-muted)' }}>{b.meta}</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {b.chips.map((ch) => (
                <span
                  key={ch.key}
                  style={{
                    fontFamily: MONO,
                    fontSize: '8.5px',
                    letterSpacing: '.06em',
                    border: '1px solid ' + ch.bd,
                    color: ch.c,
                    background: ch.bg,
                    padding: '4px 6px',
                  }}
                >
                  {ch.t}
                </span>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <div
                onClick={() => {
                  if (b.tracked) s.toast('ALREADY TRACKED');
                  else s.ptFromBrowse(b.row);
                }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid ' + b.tBd,
                  color: b.tClr,
                  background: b.tBg,
                  fontFamily: MONO,
                  fontSize: 10,
                  height: 44,
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                }}
              >
                {b.tTxt}
              </div>
              {b.hasReq ? (
                <div
                  onClick={() => window.open(b.reqUrl, '_blank')}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--acc)',
                    color: 'var(--gt-ink)',
                    fontFamily: MONO,
                    fontSize: 10,
                    fontWeight: 600,
                    height: 44,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  REQUEST ↗
                </div>
              ) : null}
            </div>
          </div>
        ))}

        <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.03em', lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
          {v.foot}
        </div>
      </div>
    </Sheet>
  );
}
